import { Component, ElementRef, HostListener, NgZone, Renderer2, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { HierarchyNode } from 'd3'
import { OrgChart } from 'd3-org-chart'
import { from } from 'rxjs'

import { BaseComponent, Grid, ListItem, ModalBackDrop, ModalSize } from '../../shared'
import { TrainingModalComponent } from './training-modal.component'
import { WageModalComponent } from './wage-modal.component'

export interface WageChange {
    reasonCode?: string
    pctIncrease: number
    effectiveDate?: string
    effectiveDateText: string
    newSalary?: number | null
}

export interface Employee {
    id: number
    firstName: string
    lastName: string
    class: string
    position: string
    hireDateString: string
    imageUrl: string
    parentId: number
    workPhone: string
    cellPhone: string
    trainingDtos: any[]
    skillDtos: any[]
    wageChanges: WageChange[]
    wageHistory: WageChange[]
    yearlyGoal?: string

    _highlighted: boolean
    _expanded: boolean
}

@Component({
    templateUrl: './org-chart.component.html',
    styleUrls: ['./org-chart.component.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule, WjGridModule, WjGridFilterModule],
})
export class OrgChartComponent extends BaseComponent {
    private el = inject(ElementRef)
    private renderer = inject(Renderer2)
    private zone = inject(NgZone)

    data: Employee[] = []
    chart!: OrgChart<any>
    search = ''
    unassignedGrid: Grid<Employee>
    missingParentGrid: Grid<Employee>
    hasTooManyRoots = false

    trainings: ListItem<string>[] = []
    selectedTraining = ''
    skills: ListItem<string>[] = []
    selectedSkill = ''

    private listenerRefs: (() => void)[] = [] // Array to store listener removal functions

    constructor() {
        super()
        this.unassignedGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.missingParentGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.refresh()
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.chart.svgHeight(innerHeight - 150)
        this.chart.data(this.chart.data()).render().fit()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.get('api/HrData/GetOrgChartData')).subscribe({
                next: successResult => {
                    this.data = successResult[0].dtos
                    this.data.forEach(d => {
                        d.wageChanges ??= []
                        d.wageHistory ??= []
                    })
                    const match = this.data.find(x => x.id == this.services.authentication.hrref)
                    if (match) match._expanded = true

                    this.unassignedGrid.items = successResult[0].nonAssigned
                    this.missingParentGrid.items = successResult[0].missingParents
                    this.hasTooManyRoots = successResult[0].hasTooManyRoots
                    this.initialLoadingComplete()

                    console.log(this.data)
                    this.buildLists()
                    this.renderOrgChart()

                    setTimeout(() => {
                        window.dispatchEvent(new Event('resize'))
                        this.chart.render().fit()
                        this.addClickListeners()
                    }, 50)
                },
            }),
        )
    }

    buildLists() {
        const trainings = this.data.flatMap(x => x.trainingDtos)
        const distinctTrainingCodes = trainings
            .map(x => x.trainCode)
            .distinct()
            .orderBy(x => x)
        const trainingList = distinctTrainingCodes.map(x => {
            const description = trainings.find(y => y.trainCode === x).description
            return new ListItem(x, x + ': ' + description)
        })
        trainingList.unshift(new ListItem(null, 'Filter by Training/Certification'))
        this.trainings = trainingList

        const skills = this.data.flatMap(x => x.skillDtos)
        const distinctSkillCodes = skills
            .map(x => x.code)
            .distinct()
            .orderBy(x => x)
        const skillList = distinctSkillCodes.map(x => {
            const description = skills.find(y => y.code === x).codeDescription
            return new ListItem(x, x + ': ' + description)
        })
        skillList.unshift(new ListItem(null, 'Filter by Skills'))
        this.skills = skillList
    }

    renderOrgChart() {
        this.chart = new OrgChart<Employee>()
            .nodeHeight((d: HierarchyNode<Employee>) => {
                let extraHeight = 0
                if (d.data.wageChanges?.length) extraHeight += 15
                if (d.data.workPhone) extraHeight += 15
                if (d.data.cellPhone) extraHeight += 15
                return 90 + 25 + extraHeight
            })
            .nodeWidth(() => 220 + 2)
            .childrenMargin(() => 50)
            .compactMarginBetween(() => 35)
            .compactMarginPair(() => 30)
            .neighbourMargin(() => 20)
            .onExpandOrCollapse(() => this.addClickListeners())
            .onZoomEnd(() => this.addClickListeners())
            .nodeContent((d: HierarchyNode<Employee>) => {
                //(d, i, arr, state)
                const color = '#FFFFFF'
                const imageDiffVert = 25 + 2

                const imageContent = d.data.imageUrl
                    ? `<img src="${d.data.imageUrl}" style="margin-left:20px;border-radius:100px;width:40px;height:40px;" />`
                    : `<div class="circle">${d.data.firstName[0]}${d.data.lastName[0]}</div>`

                return `
                            <div class='org-container' style='width:${d['width']}px;height:${d.height}px;padding-top:${
                                imageDiffVert - 2
                            }px'>
                                <div style="background-color:${color}; margin-left:-1px;width:${d['width'] - 2}px;height:${
                                    d.height - imageDiffVert
                                }px;border-radius:10px;border: 1px solid #E4E2E9">
                                    <div style="display:flex;justify-content:flex-end;margin-top:5px;margin-right:8px">#${d.data.id}</div>
                                    <div style="background-color:${color};margin-top:${
                                        -imageDiffVert - 20
                                    }px;margin-left:15px;border-radius:100px;width:50px;height:50px;"></div>
                                    <div style="margin-top:${-imageDiffVert - 20}px;">
                                        ${imageContent}
                                    </div>

                                    ${
                                        (!d.data.trainingDtos || d.data.trainingDtos.length === 0) &&
                                        (!d.data.skillDtos || d.data.skillDtos.length === 0)
                                            ? ''
                                            : `<svg id="user-training-${d.data.id}" class="training-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M173.8 5.5c11-7.3 25.4-7.3 36.4 0L228 17.2c6 3.9 13 5.8 20.1 5.4l21.3-1.3c13.2-.8 25.6 6.4 31.5 18.2l9.6 19.1c3.2 6.4 8.4 11.5 14.7 14.7L344.5 83c11.8 5.9 19 18.3 18.2 31.5l-1.3 21.3c-.4 7.1 1.5 14.2 5.4 20.1l11.8 17.8c7.3 11 7.3 25.4 0 36.4L366.8 228c-3.9 6-5.8 13-5.4 20.1l1.3 21.3c.8 13.2-6.4 25.6-18.2 31.5l-19.1 9.6c-6.4 3.2-11.5 8.4-14.7 14.7L301 344.5c-5.9 11.8-18.3 19-31.5 18.2l-21.3-1.3c-7.1-.4-14.2 1.5-20.1 5.4l-17.8 11.8c-11 7.3-25.4 7.3-36.4 0L156 366.8c-6-3.9-13-5.8-20.1-5.4l-21.3 1.3c-13.2 .8-25.6-6.4-31.5-18.2l-9.6-19.1c-3.2-6.4-8.4-11.5-14.7-14.7L39.5 301c-11.8-5.9-19-18.3-18.2-31.5l1.3-21.3c.4-7.1-1.5-14.2-5.4-20.1L5.5 210.2c-7.3-11-7.3-25.4 0-36.4L17.2 156c3.9-6 5.8-13 5.4-20.1l-1.3-21.3c-.8-13.2 6.4-25.6 18.2-31.5l19.1-9.6C65 70.2 70.2 65 73.4 58.6L83 39.5c5.9-11.8 18.3-19 31.5-18.2l21.3 1.3c7.1 .4 14.2-1.5 20.1-5.4L173.8 5.5zM272 192a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zM1.3 441.8L44.4 339.3c.2 .1 .3 .2 .4 .4l9.6 19.1c11.7 23.2 36 37.3 62 35.8l21.3-1.3c.2 0 .5 0 .7 .2l17.8 11.8c5.1 3.3 10.5 5.9 16.1 7.7l-37.6 89.3c-2.3 5.5-7.4 9.2-13.3 9.7s-11.6-2.2-14.8-7.2L74.4 455.5l-56.1 8.3c-5.7 .8-11.4-1.5-15-6s-4.3-10.7-2.1-16zm248 60.4L211.7 413c5.6-1.8 11-4.3 16.1-7.7l17.8-11.8c.2-.1 .4-.2 .7-.2l21.3 1.3c26 1.5 50.3-12.6 62-35.8l9.6-19.1c.1-.2 .2-.3 .4-.4l43.2 102.5c2.2 5.3 1.4 11.4-2.1 16s-9.3 6.9-15 6l-56.1-8.3-32.2 49.2c-3.2 5-8.9 7.7-14.8 7.2s-11-4.3-13.3-9.7z"/></svg>
`
                                    }
                                    ${
                                        d.data.yearlyGoal
                                            ? `<svg id="user-goal-${d.data.id}" class="goal-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M448 256A192 192 0 1 0 64 256a192 192 0 1 0 384 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zM256 384a128 128 0 1 0 0-256 128 128 0 1 0 0 256zm0-96a32 32 0 1 1 0-64 32 32 0 1 1 0 64z"/></svg>
`
                                            : ''
                                    }
                                                                        
                                    <div style="font-size:15px;color:#08011E;margin-left:20px;margin-top:5px"> ${d.data.firstName} ${
                                        d.data.lastName
                                    } </div>
                                    <div style="color:#716E7B;margin-left:20px;margin-top:3px;font-size:10px;"> ${d.data.position} </div>
                                    <div style="color:#716E7B;margin-left:20px;margin-top:3px;font-size:9px;">Hired: ${
                                        d.data.hireDateString
                                    } </div>
                                    ${d.data.workPhone ? `<div style="color:#716E7B;margin-left:20px;margin-top:3px;font-size:9px;">Work: ${d.data.workPhone}</div>` : ''}
                                    ${d.data.cellPhone ? `<div style="color:#716E7B;margin-left:20px;margin-top:3px;font-size:9px;">Cell: ${d.data.cellPhone}</div>` : ''}
                                    ${
                                        d.data.wageChanges?.length
                                            ? `<div id="wage-change-${d.data.id}" style="margin-left:20px;margin-top:3px;font-size:9px;cursor:pointer;color: #3366cc;text-decoration: underline;">${d.data.wageChanges[0].reasonCode}: ${d.data.wageChanges[0].effectiveDateText}</div>
`
                                            : ''
                                    }

                                </div>
                            </div>`
            })
            .container('.chart-container')
            .data(this.data)
            .render()
    }

    addClickListeners() {
        this.removeListeners()
        // Find all D3 dynamically created image elements
        this.zone.runOutsideAngular(() => {
            this.chart!.data()!.forEach((d: Employee) => {
                const imageElement = this.el.nativeElement.querySelector(`#user-training-${d.id}`)
                if (imageElement) {
                    const listener = this.renderer.listen(imageElement, 'click', () => {
                        this.zone.run(() => this.onTrainingClick(d.id))
                    })
                    this.listenerRefs.push(listener) // Store the listener function for later removal
                }

                const wageElement = this.el.nativeElement.querySelector(`#wage-change-${d.id}`)
                if (wageElement) {
                    const listener = this.renderer.listen(wageElement, 'click', () => {
                        this.zone.run(() => this.onWageClick(d.id))
                    })
                    this.listenerRefs.push(listener) // Store the listener function for later removal
                }

                const goalElement = this.el.nativeElement.querySelector(`#user-goal-${d.id}`)
                if (goalElement) {
                    const listener = this.renderer.listen(goalElement, 'click', () => {
                        this.zone.run(() => this.onGoalClick(d.id))
                    })
                    this.listenerRefs.push(listener) // Store the listener function for later removal
                }
            })
        })
    }

    onTrainingClick(userId: number) {
        const ref = this.services.modals.open(TrainingModalComponent, ModalSize.full95Percent, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as TrainingModalComponent
        const employee = this.data.find(x => x.id === userId)
        if (employee) instance.employee = employee
        from(ref.result).subscribe()
    }

    onWageClick(userId: number) {
        const ref = this.services.modals.open(WageModalComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as WageModalComponent
        const employee = this.data.find(x => x.id === userId)
        if (employee) instance.employee = employee
        from(ref.result).subscribe()
    }

    onGoalClick(userId: number) {
        const employee = this.data.find(x => x.id === userId)
        if (employee && employee.yearlyGoal) {
            // Create a tooltip element
            const tooltip = this.renderer.createElement('div')
            this.renderer.addClass(tooltip, 'goal-tooltip')
            this.renderer.setStyle(tooltip, 'position', 'fixed')
            this.renderer.setStyle(tooltip, 'background-color', '#333')
            this.renderer.setStyle(tooltip, 'color', '#fff')
            this.renderer.setStyle(tooltip, 'padding', '12px 16px')
            this.renderer.setStyle(tooltip, 'border-radius', '6px')
            this.renderer.setStyle(tooltip, 'box-shadow', '0 4px 6px rgba(0, 0, 0, 0.3)')
            this.renderer.setStyle(tooltip, 'z-index', '10000')
            this.renderer.setStyle(tooltip, 'max-width', '300px')
            this.renderer.setStyle(tooltip, 'word-wrap', 'break-word')
            this.renderer.setStyle(tooltip, 'font-size', '14px')
            this.renderer.setStyle(tooltip, 'line-height', '1.5')
            this.renderer.setStyle(tooltip, 'pointer-events', 'none')
            this.renderer.setStyle(tooltip, 'visibility', 'hidden') // Hide initially to measure

            const tooltipText = this.renderer.createText(employee.yearlyGoal)
            this.renderer.appendChild(tooltip, tooltipText)
            this.renderer.appendChild(document.body, tooltip)

            // Position tooltip near the icon with smart positioning
            const goalElement = this.el.nativeElement.querySelector(`#user-goal-${userId}`)
            if (goalElement) {
                const rect = goalElement.getBoundingClientRect()
                const tooltipRect = tooltip.getBoundingClientRect()
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight
                const padding = 10 // Padding from screen edges

                // Calculate initial position (centered below icon)
                let left = rect.left + rect.width / 2
                let top = rect.bottom + 10
                const transform = 'translateX(-50%)'

                // Check if tooltip goes off right edge
                if (left + tooltipRect.width / 2 > viewportWidth - padding) {
                    left = viewportWidth - tooltipRect.width / 2 - padding
                }

                // Check if tooltip goes off left edge
                if (left - tooltipRect.width / 2 < padding) {
                    left = tooltipRect.width / 2 + padding
                }

                // Check if tooltip goes off bottom edge - show above instead
                if (top + tooltipRect.height > viewportHeight - padding) {
                    top = rect.top - tooltipRect.height - 10
                }

                // Check if tooltip goes off top edge - show below instead
                if (top < padding) {
                    top = rect.bottom + 10
                }

                this.renderer.setStyle(tooltip, 'left', `${left}px`)
                this.renderer.setStyle(tooltip, 'top', `${top}px`)
                this.renderer.setStyle(tooltip, 'transform', transform)
                this.renderer.setStyle(tooltip, 'visibility', 'visible') // Show after positioning
            }

            // Remove tooltip on click anywhere
            const removeTooltip = () => {
                this.renderer.removeChild(document.body, tooltip)
                document.removeEventListener('click', removeTooltip)
            }
            setTimeout(() => {
                document.addEventListener('click', removeTooltip)
            }, 0)
        }
    }

    removeListeners() {
        // Call each listener reference to remove the listener
        this.listenerRefs.forEach(unlisten => unlisten())
        this.listenerRefs = [] // Clear the array
    }

    filterChart() {
        if (!this.chart) return

        // Clear previous higlighting
        this.chart.clearHighlighting()

        // Get chart nodes
        const data = this.chart.data() as any[]

        // Mark all previously expanded nodes for collapse
        data.forEach(d => (d._expanded = false))

        // Loop over data and check if input value matches any name
        data.forEach((d: Employee) => {
            if (
                (this.search !== '' &&
                    (d.firstName.toLowerCase().includes(this.search.toLowerCase()) ||
                        d.lastName.toLowerCase().includes(this.search.toLowerCase()))) ||
                (this.selectedTraining && d.trainingDtos.some(x => x.trainCode === this.selectedTraining)) ||
                (this.selectedSkill && d.skillDtos.some(x => x.code === this.selectedSkill))
            ) {
                // If matches, mark node as highlighted
                d._highlighted = true
                d._expanded = true
            }
        })

        // Update data and rerender graph
        this.chart.data(data).render().fit()
        setTimeout(() => {
            this.addClickListeners()
        }, 50)
    }

    printChart() {
        window.print()
    }
}
